import type { GSplatShaderEffectOptions } from '@/plugins/gsplat/effects/GSplatShaderEffect'
import { GSplatShaderEffect } from '@/plugins/gsplat/effects/GSplatShaderEffect'
import {
  type GSplatParticleLifecycleProgress,
  type GSplatParticleLifecycleTiming,
  type GSplatParticleLifecycleTimingInput,
  normalizeGSplatParticleLifecycleTiming,
  resolveGSplatParticleLifecycleProgress,
} from '@/plugins/gsplat/effects/particleLifecycle'

export const GSPLAT_PARTICLE_SIZE_MODES = ['fixed', 'adaptive'] as const
export type GSplatParticleSizeMode = (typeof GSPLAT_PARTICLE_SIZE_MODES)[number]

export interface GSplatParticleLifecycleEffectOptions
  extends GSplatShaderEffectOptions,
    GSplatParticleLifecycleTimingInput {
  effectScale?: number
  particleSizeMode?: GSplatParticleSizeMode
  /** 粒子球主轴的 effect-local 模型空间上限；不会放大原始最短主轴。 */
  particleTargetSize?: number
  /** 自适应模式下，粒子球目标主轴占观察参考尺度的比例。 */
  particleTargetSizeRatio?: number
  /** 自适应比例使用的 effect-local 观察参考尺度。 */
  particleSizeReferenceScale?: number
  particleOpacity?: number
  particleHighlightStrength?: number
  particleGlowStrength?: number
  particleGlowRadius?: number
  /** 粒子态局部扰动相对于 effectScale 的最大位移倍率。 */
  particleJitterAmplitude?: number
  /** 粒子态局部扰动的时间速度倍率；0 表示关闭扰动。 */
  particleJitterSpeed?: number
  scatterScale?: number
  seed?: number
  minPixelSize?: number
}

const DEFAULT_EFFECT_SCALE = 1
const DEFAULT_PARTICLE_SIZE_MODE: GSplatParticleSizeMode = 'fixed'
const DEFAULT_PARTICLE_TARGET_SIZE = 0.005
const MIN_PARTICLE_TARGET_SIZE = 0
const MAX_PARTICLE_TARGET_SIZE = 0.01
const DEFAULT_PARTICLE_TARGET_SIZE_RATIO = 0.0005
const MIN_PARTICLE_TARGET_SIZE_RATIO = 0
const MAX_PARTICLE_TARGET_SIZE_RATIO = 0.001
const DEFAULT_PARTICLE_OPACITY = 0.62
const MIN_PARTICLE_OPACITY = 0.05
const MAX_PARTICLE_OPACITY = 1
const DEFAULT_PARTICLE_HIGHLIGHT_STRENGTH = 1
const MIN_PARTICLE_HIGHLIGHT_STRENGTH = 0
const MAX_PARTICLE_HIGHLIGHT_STRENGTH = 3
const DEFAULT_PARTICLE_GLOW_STRENGTH = 0.8
const MIN_PARTICLE_GLOW_STRENGTH = 0
const MAX_PARTICLE_GLOW_STRENGTH = 3
const DEFAULT_PARTICLE_GLOW_RADIUS = 0.35
const MIN_PARTICLE_GLOW_RADIUS = 0
const MAX_PARTICLE_GLOW_RADIUS = 1
const DEFAULT_PARTICLE_JITTER_AMPLITUDE = 0.001
const MIN_PARTICLE_JITTER_AMPLITUDE = 0
const MAX_PARTICLE_JITTER_AMPLITUDE = 0.005
const DEFAULT_PARTICLE_JITTER_SPEED = 1
const MIN_PARTICLE_JITTER_SPEED = 0
const MAX_PARTICLE_JITTER_SPEED = 4
const DEFAULT_SCATTER_SCALE = 0.05
const DEFAULT_SEED = 0
const DEFAULT_MIN_PIXEL_SIZE = 0.5
const MAX_SCATTER_SCALE = 1
const MAX_SEED = 65535

const PARTICLE_LIFECYCLE_UNIFORMS_GLSL = /* glsl */ `
uniform float uParticleMix;
uniform float uScatterProgress;
uniform float uScatterScale;
uniform float uEffectScale;
uniform float uParticleSizeAdaptive;
uniform float uParticleTargetSize;
uniform float uParticleTargetSizeRatio;
uniform float uParticleSizeReferenceScale;
uniform float uParticleGlowStrength;
uniform float uParticleGlowRadius;
uniform float uParticleElapsedTime;
uniform float uParticleJitterAmplitude;
uniform float uParticleJitterSpeed;
uniform float uParticleSeed;
`

const PARTICLE_GLOW_COMMON_GLSL = /* glsl */ `
float particleGlowOuterScale(float glowStrength, float glowRadius) {
  float glowActivation = smoothstep(0.0, 0.05, max(glowStrength, 0.0));
  return 1.0 + clamp(glowRadius, 0.0, 1.0) * 1.25 * glowActivation;
}
`

const PARTICLE_JITTER_GLSL = /* glsl */ `
float particleLifecycleJitterHash(vec3 value) {
  return fract(sin(dot(value, vec3(91.7, 157.3, 269.5))) * 43758.5453123);
}

vec3 particleLifecycleJitter(vec3 originalCenter) {
  float effectScale = max(uEffectScale, 0.000001);
  vec3 local = originalCenter / effectScale;
  vec3 seedOffset = vec3(
    uParticleSeed * 0.0239,
    uParticleSeed * 0.0311,
    uParticleSeed * 0.0437
  );
  vec3 phase = vec3(
    particleLifecycleJitterHash(local + seedOffset + vec3(5.0, 17.0, 31.0)),
    particleLifecycleJitterHash(local.yzx + seedOffset + vec3(23.0, 7.0, 13.0)),
    particleLifecycleJitterHash(local.zxy + seedOffset + vec3(11.0, 29.0, 3.0))
  ) * 6.28318530718;
  float speedVariance = mix(
    0.82,
    1.18,
    particleLifecycleJitterHash(local + seedOffset + vec3(41.0, 37.0, 19.0))
  );
  float localTime = uParticleElapsedTime * uParticleJitterSpeed * speedVariance;
  vec3 wave = vec3(
    sin(localTime * 1.5 + phase.x),
    cos(localTime * 1.2 + phase.y),
    sin(localTime + phase.z)
  );
  float speedActive = step(0.000001, uParticleJitterSpeed);
  return wave
    * uParticleJitterAmplitude
    * effectScale
    * uParticleMix
    * speedActive;
}
`

const PARTICLE_LIFECYCLE_HOOKS_GLSL = /* glsl */ `
void modifySplatCenter(inout vec3 center) {
  vec3 originalCenter = center;
  center += particleLifecycleOffset(originalCenter, uScatterProgress);
  center += particleLifecycleJitter(originalCenter);
}

void modifySplatScale(inout vec3 scale) {
  if (uParticleMix <= 0.0) {
    return;
  }

  float shortestAxis = min(scale.x, min(scale.y, scale.z));
  float adaptiveTargetAxis = uParticleTargetSizeRatio * uParticleSizeReferenceScale;
  float configuredTargetAxis = mix(
    uParticleTargetSize,
    adaptiveTargetAxis,
    step(0.5, uParticleSizeAdaptive)
  );
  float targetAxis = min(shortestAxis, configuredTargetAxis);
  float glowOuterScale = particleGlowOuterScale(
    uParticleGlowStrength,
    uParticleGlowRadius
  );
  vec3 particleScale = vec3(targetAxis * glowOuterScale);
  float mixAmount = smoothstep(0.0, 1.0, uParticleMix);
  scale = mix(scale, particleScale, mixAmount);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  particleLifecycleModifyColor(center, color);
}
`

const PARTICLE_LIFECYCLE_DEFAULT_COLOR_FIELD_GLSL = /* glsl */ `
void particleLifecycleModifyColor(vec3 center, inout vec4 color) {
  // 默认保留原始颜色和 alpha。
}
`

const PARTICLE_LIFECYCLE_FRAGMENT_GLSL = /* glsl */ `
uniform float uParticleMix;
uniform float uParticleOpacity;
uniform float uParticleHighlightStrength;
uniform float uParticleGlowStrength;
uniform float uParticleGlowRadius;

${PARTICLE_GLOW_COMMON_GLSL}

void modifySplatFragment(
  vec2 gaussianUv,
  vec4 sourceColor,
  inout vec3 premultipliedColor,
  inout float alpha
) {
  float mixAmount = smoothstep(0.0, 1.0, uParticleMix);
  if (mixAmount <= 0.0) {
    return;
  }

  float radiusSquared = dot(gaussianUv, gaussianUv);
  float radius = sqrt(radiusSquared);
  float glowEnergy = 1.0 - exp(-max(uParticleGlowStrength, 0.0) * 0.75);
  float glowOuterScale = particleGlowOuterScale(
    uParticleGlowStrength,
    uParticleGlowRadius
  );
  float coreRadius = 1.0 / glowOuterScale;
  vec2 sphereUv = gaussianUv / coreRadius;
  float sphereRadiusSquared = dot(sphereUv, sphereUv);
  float sphereDepth = sqrt(max(0.0, 1.0 - sphereRadiusSquared));
  vec3 sphereNormal = normalize(vec3(sphereUv.x, -sphereUv.y, sphereDepth));
  vec3 lightDirection = normalize(vec3(-0.45, 0.55, 0.82));
  float lightFacing = max(dot(sphereNormal, lightDirection), 0.0);
  float glint = pow(lightFacing, 40.0);
  float fresnel = pow(1.0 - sphereDepth, 2.0);

  float edgeWidth = max(fwidth(radius) * 1.5, 0.0015);
  float circleCoverage = 1.0 - smoothstep(1.0 - edgeWidth, 1.0, radius);
  float coreCoverage = 1.0 - smoothstep(
    coreRadius - edgeWidth,
    coreRadius + edgeWidth,
    radius
  );
  coreCoverage *= circleCoverage;
  float haloProgress = clamp(
    (radius - coreRadius) / max(1.0 - coreRadius, 0.001),
    0.0,
    1.0
  );
  float haloProfile = pow(max(1.0 - haloProgress, 0.0), 2.0)
    * circleCoverage
    * (1.0 - coreCoverage);
  float opacityProfile = mix(0.55, 0.92, fresnel);
  float coreAlpha = sourceColor.a
    * coreCoverage
    * clamp(
      uParticleOpacity * opacityProfile
        + glint * 0.12 * uParticleHighlightStrength,
      0.0,
      1.0
    );
  float haloAlpha = sourceColor.a * haloProfile * glowEnergy * 0.025;
  float particleAlpha = clamp(coreAlpha + haloAlpha, 0.0, 1.0);

  vec3 coolHighlight = vec3(0.78, 0.92, 1.0);
  vec3 warmGlint = vec3(1.0, 0.98, 0.92);
  vec3 particleColor = mix(sourceColor.rgb, coolHighlight, fresnel * 0.28);
  particleColor += coolHighlight * fresnel * 0.08 * uParticleHighlightStrength;
  particleColor += warmGlint * glint * 0.38 * uParticleHighlightStrength;

  vec3 glowColor = mix(max(sourceColor.rgb, vec3(0.02)), coolHighlight, 0.45);
  float glowOpacity = clamp(uParticleOpacity, 0.0, 1.0);
  vec3 coreGlowContribution = glowColor
    * sourceColor.a
    * coreCoverage
    * glowEnergy
    * glowOpacity
    * 0.18;
  vec3 haloGlowContribution = glowColor
    * sourceColor.a
    * haloProfile
    * glowEnergy
    * glowOpacity
    * 0.24;
  vec3 particlePremultipliedColor = particleColor * coreAlpha
    + coreGlowContribution
    + haloGlowContribution;
  alpha = mix(alpha, particleAlpha, mixAmount);
  premultipliedColor = mix(
    premultipliedColor,
    particlePremultipliedColor,
    mixAmount
  );
}
`

const PARTICLE_GLOW_COMMON_WGSL = /* wgsl */ `
fn particleGlowOuterScale(glowStrength: f32, glowRadius: f32) -> f32 {
  let glowActivation = smoothstep(0.0, 0.05, max(glowStrength, 0.0));
  return 1.0 + clamp(glowRadius, 0.0, 1.0) * 1.25 * glowActivation;
}
`

const PARTICLE_JITTER_WGSL = /* wgsl */ `
fn particleLifecycleJitterHash(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(91.7, 157.3, 269.5))) * 43758.5453123);
}

fn particleLifecycleJitter(originalCenter: vec3f) -> vec3f {
  let effectScale = max(uniform.uEffectScale, 0.000001);
  let local = originalCenter / effectScale;
  let seedOffset = vec3f(
    uniform.uParticleSeed * 0.0239,
    uniform.uParticleSeed * 0.0311,
    uniform.uParticleSeed * 0.0437
  );
  let phase = vec3f(
    particleLifecycleJitterHash(local + seedOffset + vec3f(5.0, 17.0, 31.0)),
    particleLifecycleJitterHash(local.yzx + seedOffset + vec3f(23.0, 7.0, 13.0)),
    particleLifecycleJitterHash(local.zxy + seedOffset + vec3f(11.0, 29.0, 3.0))
  ) * 6.28318530718;
  let speedVariance = mix(
    0.82,
    1.18,
    particleLifecycleJitterHash(local + seedOffset + vec3f(41.0, 37.0, 19.0))
  );
  let localTime = uniform.uParticleElapsedTime * uniform.uParticleJitterSpeed * speedVariance;
  let wave = vec3f(
    sin(localTime * 1.5 + phase.x),
    cos(localTime * 1.2 + phase.y),
    sin(localTime + phase.z)
  );
  let speedActive = step(0.000001, uniform.uParticleJitterSpeed);
  return wave * uniform.uParticleJitterAmplitude * effectScale * uniform.uParticleMix * speedActive;
}
`

const PARTICLE_LIFECYCLE_HOOKS_WGSL = /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {
  let originalCenter = *center;
  *center = *center + particleLifecycleOffset(originalCenter, uniform.uScatterProgress);
  *center = *center + particleLifecycleJitter(originalCenter);
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  if (uniform.uParticleMix <= 0.0) {
    return;
  }
  let shortestAxis = min((*scale).x, min((*scale).y, (*scale).z));
  let adaptiveTargetAxis = uniform.uParticleTargetSizeRatio * uniform.uParticleSizeReferenceScale;
  let configuredTargetAxis = mix(
    uniform.uParticleTargetSize,
    adaptiveTargetAxis,
    step(0.5, uniform.uParticleSizeAdaptive)
  );
  let targetAxis = min(shortestAxis, configuredTargetAxis);
  let glowOuterScale = particleGlowOuterScale(
    uniform.uParticleGlowStrength,
    uniform.uParticleGlowRadius
  );
  let particleScale = vec3f(targetAxis * glowOuterScale);
  let mixAmount = smoothstep(0.0, 1.0, uniform.uParticleMix);
  *scale = mix(*scale, particleScale, vec3f(mixAmount));
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  particleLifecycleModifyColor(center, color);
}
`

const PARTICLE_LIFECYCLE_DEFAULT_COLOR_FIELD_WGSL = /* wgsl */ `
fn particleLifecycleModifyColor(center: vec3f, color: ptr<function, vec4f>) {
  // 默认保留原始颜色和 alpha。
}
`

const PARTICLE_LIFECYCLE_FRAGMENT_WGSL = /* wgsl */ `
fn modifySplatFragment(gaussianFootprint: vec2f, color: ptr<function, vec4f>) {
  let mixAmount = smoothstep(0.0, 1.0, uniform.uParticleMix);
  if (mixAmount <= 0.0) {
    return;
  }

  let radiusSquared = dot(gaussianFootprint, gaussianFootprint);
  let radius = sqrt(radiusSquared);
  let glowEnergy = 1.0 - exp(-max(uniform.uParticleGlowStrength, 0.0) * 0.75);
  let glowOuterScale = particleGlowOuterScale(
    uniform.uParticleGlowStrength,
    uniform.uParticleGlowRadius
  );
  let coreRadius = 1.0 / glowOuterScale;
  let sphereUv = gaussianFootprint / coreRadius;
  let sphereRadiusSquared = dot(sphereUv, sphereUv);
  let sphereDepth = sqrt(max(0.0, 1.0 - sphereRadiusSquared));
  let sphereNormal = normalize(vec3f(sphereUv.x, -sphereUv.y, sphereDepth));
  let lightDirection = normalize(vec3f(-0.45, 0.55, 0.82));
  let lightFacing = max(dot(sphereNormal, lightDirection), 0.0);
  let glint = pow(lightFacing, 40.0);
  let fresnel = pow(1.0 - sphereDepth, 2.0);

  let edgeWidth = max(fwidth(radius) * 1.5, 0.0015);
  let circleCoverage = 1.0 - smoothstep(1.0 - edgeWidth, 1.0, radius);
  var coreCoverage = 1.0 - smoothstep(coreRadius - edgeWidth, coreRadius + edgeWidth, radius);
  coreCoverage = coreCoverage * circleCoverage;
  let haloProgress = clamp(
    (radius - coreRadius) / max(1.0 - coreRadius, 0.001),
    0.0,
    1.0
  );
  let haloProfile = pow(max(1.0 - haloProgress, 0.0), 2.0) * circleCoverage * (1.0 - coreCoverage);
  let opacityProfile = mix(0.55, 0.92, fresnel);
  let sourceAlpha = (*color).a;
  let coreAlpha = sourceAlpha
    * coreCoverage
    * clamp(
      uniform.uParticleOpacity * opacityProfile
        + glint * 0.12 * uniform.uParticleHighlightStrength,
      0.0,
      1.0
    );
  let haloAlpha = sourceAlpha * haloProfile * glowEnergy * 0.025;
  let particleAlpha = clamp(coreAlpha + haloAlpha, 0.0, 1.0);

  let coolHighlight = vec3f(0.78, 0.92, 1.0);
  let warmGlint = vec3f(1.0, 0.98, 0.92);
  var particleColor = mix((*color).rgb, coolHighlight, vec3f(fresnel * 0.28));
  particleColor = particleColor + coolHighlight * (fresnel * 0.08 * uniform.uParticleHighlightStrength);
  particleColor = particleColor + warmGlint * (glint * 0.38 * uniform.uParticleHighlightStrength);

  let glowColor = mix(max((*color).rgb, vec3f(0.02)), coolHighlight, vec3f(0.45));
  let glowOpacity = clamp(uniform.uParticleOpacity, 0.0, 1.0);
  let coreGlowContribution = glowColor
    * (sourceAlpha * coreCoverage * glowEnergy * glowOpacity * 0.18);
  let haloGlowContribution = glowColor
    * (sourceAlpha * haloProfile * glowEnergy * glowOpacity * 0.24);
  let particlePremultipliedColor =
    particleColor * coreAlpha + coreGlowContribution + haloGlowContribution;
  // WebGPU 片元钩子接收直写色 + 每 splat alpha，premultiply 由材质在钩子之后完成，
  // 因此这里把「预乘粒子色 / 粒子 alpha」还原成直写色，保持与 WebGL2 预乘结果一致。
  let straightColor = particlePremultipliedColor / max(particleAlpha, 0.0001);
  *color = vec4f(
    mix((*color).rgb, straightColor, vec3f(mixAmount)),
    mix(sourceAlpha, particleAlpha, mixAmount)
  );
}
`

/**
 * 可逆 GSplat 粒子生命周期基础层。
 *
 * 子类只需要提供 `particleLifecycleOffset` GLSL 函数，形态转换、时间线和恢复由该类统一处理。
 */
export abstract class GSplatParticleLifecycleEffect extends GSplatShaderEffect {
  readonly timing: GSplatParticleLifecycleTiming
  readonly effectScale: number
  readonly particleSizeMode: GSplatParticleSizeMode
  readonly particleTargetSize: number
  readonly particleTargetSizeRatio: number
  readonly particleSizeReferenceScale: number
  readonly particleOpacity: number
  readonly particleHighlightStrength: number
  readonly particleGlowStrength: number
  readonly particleGlowRadius: number
  readonly particleJitterAmplitude: number
  readonly particleJitterSpeed: number
  readonly scatterScale: number
  readonly seed: number
  readonly minPixelSize: number

  protected constructor(options: GSplatParticleLifecycleEffectOptions = {}) {
    super(options)

    this.timing = normalizeGSplatParticleLifecycleTiming(options)
    this.effectScale = normalizePositive(options.effectScale, DEFAULT_EFFECT_SCALE)
    this.particleSizeMode = normalizeParticleSizeMode(options.particleSizeMode)
    this.particleTargetSize = normalizeRange(
      options.particleTargetSize,
      MIN_PARTICLE_TARGET_SIZE,
      MAX_PARTICLE_TARGET_SIZE,
      DEFAULT_PARTICLE_TARGET_SIZE
    )
    this.particleTargetSizeRatio = normalizeRange(
      options.particleTargetSizeRatio,
      MIN_PARTICLE_TARGET_SIZE_RATIO,
      MAX_PARTICLE_TARGET_SIZE_RATIO,
      DEFAULT_PARTICLE_TARGET_SIZE_RATIO
    )
    this.particleSizeReferenceScale = normalizePositive(
      options.particleSizeReferenceScale,
      this.effectScale
    )
    this.particleOpacity = normalizeRange(
      options.particleOpacity,
      MIN_PARTICLE_OPACITY,
      MAX_PARTICLE_OPACITY,
      DEFAULT_PARTICLE_OPACITY
    )
    this.particleHighlightStrength = normalizeRange(
      options.particleHighlightStrength,
      MIN_PARTICLE_HIGHLIGHT_STRENGTH,
      MAX_PARTICLE_HIGHLIGHT_STRENGTH,
      DEFAULT_PARTICLE_HIGHLIGHT_STRENGTH
    )
    this.particleGlowStrength = normalizeRange(
      options.particleGlowStrength,
      MIN_PARTICLE_GLOW_STRENGTH,
      MAX_PARTICLE_GLOW_STRENGTH,
      DEFAULT_PARTICLE_GLOW_STRENGTH
    )
    this.particleGlowRadius = normalizeRange(
      options.particleGlowRadius,
      MIN_PARTICLE_GLOW_RADIUS,
      MAX_PARTICLE_GLOW_RADIUS,
      DEFAULT_PARTICLE_GLOW_RADIUS
    )
    this.particleJitterAmplitude = normalizeRange(
      options.particleJitterAmplitude,
      MIN_PARTICLE_JITTER_AMPLITUDE,
      MAX_PARTICLE_JITTER_AMPLITUDE,
      DEFAULT_PARTICLE_JITTER_AMPLITUDE
    )
    this.particleJitterSpeed = normalizeRange(
      options.particleJitterSpeed,
      MIN_PARTICLE_JITTER_SPEED,
      MAX_PARTICLE_JITTER_SPEED,
      DEFAULT_PARTICLE_JITTER_SPEED
    )
    this.scatterScale = normalizeRange(
      options.scatterScale,
      0,
      MAX_SCATTER_SCALE,
      DEFAULT_SCATTER_SCALE
    )
    this.seed = Math.round(normalizeRange(options.seed, 0, MAX_SEED, DEFAULT_SEED))
    this.minPixelSize = normalizeNonNegative(options.minPixelSize, DEFAULT_MIN_PIXEL_SIZE)
    this.uniforms.minPixelSize = { value: this.minPixelSize }
  }

  override getShaderGLSL(): string {
    return `${PARTICLE_LIFECYCLE_UNIFORMS_GLSL}\n${PARTICLE_GLOW_COMMON_GLSL}\n${PARTICLE_JITTER_GLSL}\n${this.getParticleMotionFieldGLSL()}\n${this.getParticleColorFieldGLSL()}\n${PARTICLE_LIFECYCLE_HOOKS_GLSL}`
  }

  override getShaderWGSL(): string {
    return `${PARTICLE_GLOW_COMMON_WGSL}\n${PARTICLE_JITTER_WGSL}\n${this.getParticleMotionFieldWGSL()}\n${this.getParticleColorFieldWGSL()}\n${PARTICLE_LIFECYCLE_HOOKS_WGSL}`
  }

  override getFragmentShaderGLSL(): string {
    return PARTICLE_LIFECYCLE_FRAGMENT_GLSL
  }

  override getFragmentShaderWGSL(): string {
    return `${PARTICLE_GLOW_COMMON_WGSL}\n${PARTICLE_LIFECYCLE_FRAGMENT_WGSL}`
  }

  getCompletionTime(): number {
    return this.timing.duration
  }

  isEffectComplete(): boolean {
    return this.effectTime >= this.timing.duration
  }

  getLifecycleProgress(elapsedTime = this.effectTime): GSplatParticleLifecycleProgress {
    return resolveGSplatParticleLifecycleProgress(elapsedTime, this.timing)
  }

  override setElapsedTime(seconds: number): void {
    if (!this.enabled && seconds >= this.timing.duration) {
      this.effectTime = seconds
      return
    }
    if (!this.enabled) {
      this.enable()
    }
    super.setElapsedTime(seconds)
  }

  protected override updateEffect(effectTime: number): void {
    const progress = this.getLifecycleProgress(effectTime)
    this.setUniform('uParticleMix', progress.particleMix)
    this.setUniform('uScatterProgress', progress.scatterProgress)
    this.setUniform('uScatterScale', this.scatterScale)
    this.setUniform('uEffectScale', this.effectScale)
    this.setUniform('uParticleSizeAdaptive', this.particleSizeMode === 'adaptive' ? 1 : 0)
    this.setUniform('uParticleTargetSize', this.particleTargetSize)
    this.setUniform('uParticleTargetSizeRatio', this.particleTargetSizeRatio)
    this.setUniform('uParticleSizeReferenceScale', this.particleSizeReferenceScale)
    this.setUniform('uParticleOpacity', this.particleOpacity)
    this.setUniform('uParticleHighlightStrength', this.particleHighlightStrength)
    this.setUniform('uParticleGlowStrength', this.particleGlowStrength)
    this.setUniform('uParticleGlowRadius', this.particleGlowRadius)
    this.setUniform('uParticleElapsedTime', progress.elapsedTime)
    this.setUniform('uParticleJitterAmplitude', this.particleJitterAmplitude)
    this.setUniform('uParticleJitterSpeed', this.particleJitterSpeed)
    this.setUniform('uParticleSeed', this.seed)
    this.setUniform('minPixelSize', this.minPixelSize)

    if (progress.complete) {
      this.finishWithShadersAttached()
    }
  }

  protected abstract getParticleMotionFieldGLSL(): string

  protected getParticleColorFieldGLSL(): string {
    return PARTICLE_LIFECYCLE_DEFAULT_COLOR_FIELD_GLSL
  }

  protected abstract getParticleMotionFieldWGSL(): string

  protected getParticleColorFieldWGSL(): string {
    return PARTICLE_LIFECYCLE_DEFAULT_COLOR_FIELD_WGSL
  }
}

function normalizeParticleSizeMode(
  value: GSplatParticleSizeMode | undefined
): GSplatParticleSizeMode {
  return value === 'adaptive' || value === 'fixed' ? value : DEFAULT_PARTICLE_SIZE_MODE
}

function normalizePositive(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback
}

function normalizeNonNegative(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback
}

function normalizeRange(
  value: number | undefined,
  min: number,
  max: number,
  fallback: number
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }
  return Math.min(max, Math.max(min, value))
}
